import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, ButtonBase, Menu, MenuItem, styled } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconCheck, IconChevronDown } from '@tabler/icons-react';

import { formatToDateAndTime, Typography } from '@zendesk/forethought-elements';
import VariantPill from 'src/components/dashboard-pages/solve-insights/common/VariantPill';
import { useSelectedAutoflowVersion } from 'src/hooks/useSelectedAutoflowVersion';
import { useVersionHistory } from 'src/pages/workflow-builder-edit/autonomous-agent/new-version-history/useVersionHistory';
import {
  getVersionDisplayName,
  isAutoSavedVersion,
} from 'src/pages/workflow-builder-edit/autonomous-agent/new-version-history/utils';
import {
  useVariantLookup,
  variantLookupKey,
} from 'src/pages/workflow-builder-edit/utils/buildVariantLookup';
import { selectIsWorkflowActive } from 'src/slices/canvas-workflow-builder/workflowBuilderSlice';
import { WorkflowVersionType } from 'src/slices/workflow-preview/types';

interface VersionDropdownProps {
  intentWorkflowId: string;
}

export const VersionDropdown = ({ intentWorkflowId }: VersionDropdownProps) => {
  const { palette } = useTheme();
  const { t } = useTranslation('workflow-builder-edit');
  const { clearSelectedVersion, selectedVersion, setSelectedVersion } =
    useSelectedAutoflowVersion();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const isWorkflowActive = useSelector(selectIsWorkflowActive);

  const {
    currentVersions,
    isAutoSaved,
    namedVersions,
    publishedVersions,
    versionHistory,
  } = useVersionHistory({ intentWorkflowId });

  const variantLookup = useVariantLookup(intentWorkflowId);

  // Primary current version for status badge display
  const primaryCurrentVersion = currentVersions[0];

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleVersionSelect = (
    version: number | null,
    versionType?: WorkflowVersionType,
  ) => {
    if (version === null) {
      clearSelectedVersion();
    } else {
      setSelectedVersion(version, versionType);
    }
    handleClose();
  };

  const isLive =
    primaryCurrentVersion?.is_published === true && isWorkflowActive;

  const isSelectedVersion = (version: (typeof versionHistory)[number]) =>
    selectedVersion !== null &&
    selectedVersion.version === version.version &&
    (selectedVersion.versionType
      ? selectedVersion.versionType === version.version_type
      : true);

  const getStatusBadgeText = () => {
    if (selectedVersion === null) {
      // Current version - show name if it exists, otherwise Live/Offline
      if (isAutoSaved) {
        return t('header.versionDropdown.autoSaved');
      }
      return isLive
        ? t('header.versionDropdown.live')
        : t('header.versionDropdown.offline');
    }
    // Historical version - show "Published • date"
    const selected =
      versionHistory.find(
        v =>
          v.version === selectedVersion.version &&
          (selectedVersion.versionType
            ? v.version_type === selectedVersion.versionType
            : true),
      ) || versionHistory.find(v => v.version === selectedVersion.version);

    if (selected) {
      // Auto-saved versions show "Auto-Saved"
      if (isAutoSavedVersion(selected)) {
        return t('header.versionDropdown.autoSaved');
      }
      // Saved versions show their name
      if (selected.version_type === 'saved') {
        return getVersionDisplayName(selected);
      }
      // Published versions show date
      return t('header.versionDropdown.publishedAt', {
        date: formatToDateAndTime(selected.last_updated_at),
      });
    }
    return t('header.versionDropdown.version', {
      version: selectedVersion.version,
    });
  };

  const isCurrentVersionSelected = (
    version: (typeof currentVersions)[number],
  ) =>
    selectedVersion !== null &&
    selectedVersion.version === version.version &&
    selectedVersion.versionType === version.version_type;

  const getVersionLabel = (
    version: (typeof currentVersions)[number],
    index: number,
  ) => {
    if (version.version_type === 'auto_saved') {
      return t('header.versionDropdown.autoSaved');
    }
    const versionIsLive = version.is_published === true && isWorkflowActive;
    if (index === 0) {
      return versionIsLive
        ? t('header.versionDropdown.live')
        : t('header.versionDropdown.offline');
    }
    return (
      getVersionDisplayName(version) ||
      t('header.versionDropdown.version', { version: version.version })
    );
  };

  const menuItems = [
    <SectionLabel key='current-label'>
      {t('header.versionDropdown.current')}
    </SectionLabel>,
    ...currentVersions.map((version, index) => {
      const variantInfo = variantLookup.get(
        variantLookupKey(version.version, version.version_type),
      );
      const isSelected =
        selectedVersion === null
          ? index === 0
          : isCurrentVersionSelected(version);

      return (
        <StyledMenuItem
          isSelected={isSelected}
          key={`current-${version.version}-${version.version_type}`}
          onClick={() =>
            handleVersionSelect(version.version, version.version_type)
          }
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: '8px',
              }}
            >
              {getVersionLabel(version, index)}
              {variantInfo && (
                <VariantPill shouldShowOnlyOnActive variant={variantInfo} />
              )}
            </Box>
            {isSelected ? (
              <IconCheck color={palette.colors.teal[500]} size={20} />
            ) : null}
          </Box>
        </StyledMenuItem>
      );
    }),
    ...(namedVersions.length > 0
      ? [
          <SectionLabel key='versions-label'>
            {t('header.versionDropdown.versions')}
          </SectionLabel>,
          ...namedVersions.map(version => {
            const variantInfo = variantLookup.get(
              variantLookupKey(version.version, version.version_type),
            );
            return (
              <StyledMenuItem
                isSelected={isSelectedVersion(version)}
                key={`${version.version}-${version.version_type}`}
                onClick={() =>
                  handleVersionSelect(version.version, version.version_type)
                }
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      gap: '8px',
                    }}
                  >
                    {getVersionDisplayName(version)}
                    {variantInfo && (
                      <VariantPill
                        shouldShowOnlyOnActive
                        variant={variantInfo}
                      />
                    )}
                  </Box>
                  {isSelectedVersion(version) ? (
                    <IconCheck color={palette.colors.teal[500]} size={20} />
                  ) : null}
                </Box>
              </StyledMenuItem>
            );
          }),
        ]
      : []),
    ...(publishedVersions.length > 0
      ? [
          <SectionLabel key='history-label'>
            {t('header.versionDropdown.publishHistory')}
          </SectionLabel>,
          ...publishedVersions.map(version => {
            const variantInfo = variantLookup.get(
              variantLookupKey(version.version, version.version_type),
            );
            return (
              <StyledMenuItem
                isSelected={isSelectedVersion(version)}
                key={`${version.version}-${version.version_type}`}
                onClick={() =>
                  handleVersionSelect(version.version, version.version_type)
                }
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      gap: '8px',
                    }}
                  >
                    {formatToDateAndTime(version.last_updated_at)}
                    {variantInfo && (
                      <VariantPill
                        shouldShowOnlyOnActive
                        variant={variantInfo}
                      />
                    )}
                  </Box>
                  {isSelectedVersion(version) ? (
                    <IconCheck color={palette.colors.teal[500]} size={18} />
                  ) : null}
                </Box>
              </StyledMenuItem>
            );
          }),
        ]
      : []),
  ];

  return (
    <Box
      sx={{
        borderRadius: '4px',
        display: 'flex',
      }}
    >
      {/* Left side: Badge with version name */}
      <VersionBadge>
        <StatusBadge isLive={selectedVersion === null && isLive}>
          <Typography color={palette.colors.white} variant='font12Medium'>
            {getStatusBadgeText()}
          </Typography>
        </StatusBadge>
      </VersionBadge>

      {/* Right side: Chevron down button */}
      <ChevronButton
        aria-controls={open ? 'version-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup='true'
        onClick={handleClick}
      >
        <IconChevronDown color={palette.colors.grey[700]} size={20} />
      </ChevronButton>

      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        id='version-menu'
        onClose={handleClose}
        open={open}
        slotProps={{
          paper: {
            sx: {
              marginTop: '4px',
              minWidth: '280px',
            },
          },
        }}
        sx={{
          '& .MuiList-root': {
            paddingBottom: 0,
            paddingTop: 0,
          },
        }}
        transformOrigin={{
          horizontal: 'left',
          vertical: 'top',
        }}
      >
        {menuItems}
      </Menu>
    </Box>
  );
};

const VersionBadge = styled(ButtonBase)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 8px;
  border-radius: 4px;
  height: 32px;
  min-width: unset;
  text-transform: none;
`;

const ChevronButton = styled(ButtonBase)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 4px;
  height: 32px;
  width: 32px;
  margin-left: 1px;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.palette.colors.teal[200]};
  }
`;

const SectionLabel = styled(Box)`
  padding: 8px 16px;
  color: ${props => props.theme.palette.colors.grey[500]};
  font-size: 14px;
  font-weight: 400;
`;

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: prop => prop !== 'isSelected',
})<{ isSelected?: boolean }>`
  font-size: 14px;
  font-weight: 400;
  padding: 8px 16px;
  color: ${props =>
    props.isSelected ? props.theme.palette.colors.teal[500] : 'inherit'};
  &:hover {
    background: ${props => props.theme.palette.colors.teal[100]};
  }
`;

const StatusBadge = styled(Box, {
  shouldForwardProp: prop => prop !== 'isLive',
})<{ isLive: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
  background: ${props =>
    props.isLive
      ? props.theme.palette.colors.green[500]
      : props.theme.palette.colors.grey[500]};
`;
